import { PortalBox } from "../portal/PortalAdmin";
import { Attachments } from "../files/Attachments";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { partiesApi } from "../lib/api";
import { useCompany } from "../masters/reference";
import { useLoad } from "../ui/useLoad";
import { ErrorNote, Loading, PageHead } from "../ui/bits";
import { fmtAmount } from "../lib/money";
import { fuzzyFilter } from "../lib/fuzzy";
import { fmtDate } from "../companies/CompanyPicker";
import { toUrl } from "../shell/routes";
import { PartyForm } from "./PartyForm";
import { emptyParty, fromParty } from "./partyDraft";

// CU10 Customers (Sales) and VE10 Vendors (Purchases): everyone you sell to or buy from, with what they owe or are owed;
// CU01 / VE01 a new one, and the same form to change one.
type Side = "customer" | "supplier";
const K: Record<Side, { title: string; one: string; crumb: string; list: string; code: string; newCode: string; owe: string }> = {
  customer: { title: "Customers", one: "customer", crumb: "Sales", list: "parties/customers", code: "CU10", newCode: "CU01", owe: "Receivable" },
  supplier: { title: "Vendors", one: "vendor", crumb: "Purchases", list: "parties/vendors", code: "VE10", newCode: "VE01", owe: "Payable" },
};

export function PartiesPage({ side }: { side: Side }) {
  const { company } = useCompany();
  const k = K[side];
  const { data, error } = useLoad(() => partiesApi.list(company.id, side), [company.id, side]);
  const [q, setQ] = useState("");
  const [inactive, setInactive] = useState(false);
  const rows = useMemo(() => {
    const all = (data ?? []).filter((r) => inactive || r.active);
    return q.trim() ? fuzzyFilter(all, q, (r) => `${r.name} ${r.legal_name ?? ""} ${r.gstin ?? ""} ${r.place ?? ""} ${r.phone ?? ""} ${r.email ?? ""}`, 1000) : all;
  }, [data, q, inactive]);
  const sign = side === "customer" ? 1 : -1;   // what they owe us is a debit; what we owe a vendor is a credit
  const total = rows.reduce((t, r) => t + r.balance * sign, 0);
  return (
    <section className="page">
      <PageHead crumb={k.crumb} title={k.title} code={k.code}>
        <Link className="button primary" to={toUrl(`${k.list}/new`)}><Plus size={15} aria-hidden="true" /> New {k.one}</Link>
      </PageHead>
      <ErrorNote text={error?.message} />
      {!data ? <Loading /> : data.length === 0 ? (
        <div className="card empty-card"><p>No {k.title.toLowerCase()} yet. <Link to={toUrl(`${k.list}/new`)}>Add the first one</Link>: type a GSTIN and the rest fills in,
          or create one straight from an {side === "customer" ? "invoice" : "bill"} by typing a new name.</p></div>
      ) : <>
        <div className="toolbar">
          <input autoFocus placeholder="Filter: name, GSTIN, city, phone" value={q} onChange={(e) => setQ(e.target.value)} aria-label={`Filter ${k.title.toLowerCase()}`} />
          <label className="check small"><input type="checkbox" checked={inactive} onChange={(e) => setInactive(e.target.checked)} /> Show inactive</label>
          <span className="muted small">{rows.length} of {data.length} · {k.owe.toLowerCase()} {fmtAmount(total)}</span>
        </div>
        <table className="grid-table card">
          <thead><tr><th>Name</th><th>GSTIN</th><th>Place</th><th>Contact</th><th>Last entry</th><th className="num">{k.owe}</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id} className={r.active ? "" : "muted"}>
              <td><Link to={toUrl(`${k.list}/${r.id}`)}>{r.name}</Link>{r.kind === "both" && <span className="tag">both</span>}{r.currency && <span className="tag">{r.currency}</span>}
                {r.legal_name && r.legal_name !== r.name && <div className="muted small">{r.legal_name}</div>}</td>
              <td className="mono small">{r.gstin ?? ""}</td>
              <td className="small">{[r.place, r.state].filter(Boolean).join(", ")}</td>
              <td className="small">{[r.contact_person, r.phone, r.email].filter(Boolean).join(" · ")}</td>
              <td className="small">{r.last_entry ? fmtDate(r.last_entry) : ""}</td>
              <td className="num">{r.balance ? `${fmtAmount(Math.abs(r.balance))} ${r.balance > 0 ? "Dr" : "Cr"}` : ""}</td>
            </tr>))}</tbody>
        </table>
      </>}
    </section>
  );
}

export function PartyPage({ side }: { side: Side }) {
  const { company, ref } = useCompany();
  const { partyId } = useParams();
  const nav = useNavigate();
  const k = K[side];
  const isNew = !partyId || partyId === "new";
  const { data, error } = useLoad(async () => (isNew ? null : partiesApi.get(company.id, partyId!)), [company.id, partyId]);
  const [saved, setSaved] = useState<string | null>(null);
  if (error) return <section className="page"><PageHead crumb={`${k.crumb} › ${k.title}`} title={k.title} /><ErrorNote text={error.message} /></section>;
  if (!isNew && !data) return <section className="page"><PageHead crumb={`${k.crumb} › ${k.title}`} title={k.title} /><Loading /></section>;
  const closed = ref?.opening_year?.closed_reason ?? null;
  return (
    <section className="page narrow">
      <PageHead crumb={`${k.crumb} › ${k.title}`} title={isNew ? `New ${k.one}` : data!.name} code={isNew ? k.newCode : undefined} />
      {saved && <p className="msg ok">{saved}</p>}
      {data && (
        <p className="muted">
          {data.balance ? <>{data.balance > 0 ? "Owes us" : "We owe"} <b>{fmtAmount(Math.abs(data.balance))}</b> · </> : null}
          <Link to={toUrl(`${k.list}/${data.id}/statement`)}>Statement</Link> · <Link to={toUrl(`reports/r/ledger?ledger=${data.id}`)}>Account</Link> · <Link to={toUrl("reports/r/outstanding")}>Open bills</Link> ·{" "}
          <Link to={toUrl(`masters/ledgers/${data.id}`)}>More settings</Link> <span className="small">(TDS, opening bills, account number)</span>
        </p>
      )}
      <PartyForm key={data?.id ?? "new"} initial={data ? fromParty(data) : emptyParty(side)} openingsClosed={data?.openings_closed ?? closed}
        onSaved={(p) => { if (isNew) nav(toUrl(`${k.list}/${p.id}`), { replace: true }); setSaved(`Saved “${p.name}”.`); }}
        onCancel={() => nav(toUrl(k.list))} />
      {data && !isNew && <PortalBox partyId={data.id} />}
      {data && !isNew && <section className="card panel"><Attachments companyId={company.id} kind="ledger" id={data.id} canEdit={data.can.edit} /></section>}
    </section>
  );
}
